import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, FileText, Shield, Truck, Building2, CheckCircle2, XCircle } from 'lucide-react';

export default function DocumentManagement() {
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [filter, setFilter] = useState('all');
  const [form, setForm] = useState({
    name: '',
    description: '',
    category: 'driver',
    is_mandatory: false,
    accepted_formats: ['PDF', 'JPG', 'PNG'],
    max_file_size_mb: 5,
    expiry_required: false
  });

  useEffect(() => {
    fetchDocuments();
    fetchCategories();
  }, []);

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/document-types');
      setDocuments(response.data.document_types || []);
    } catch (err) {
      toast.error('Failed to load document types');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/admin/document-types/categories');
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      category: 'driver',
      is_mandatory: false,
      accepted_formats: ['PDF', 'JPG', 'PNG'],
      max_file_size_mb: 5,
      expiry_required: false
    });
    setEditingDoc(null);
  };

  const handleCreate = async () => {
    if (!form.name.trim()) {
      toast.error('Document name is required');
      return;
    }

    try {
      await api.post('/admin/document-types', form);
      toast.success('Document type created');
      setShowDialog(false);
      resetForm();
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create document type');
    }
  };

  const handleEdit = async () => {
    try {
      await api.put(`/admin/document-types/${editingDoc.id}`, form);
      toast.success('Document type updated');
      setShowDialog(false);
      resetForm();
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to update document type');
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('Are you sure you want to deactivate this document type?')) return;

    try {
      await api.delete(`/admin/document-types/${docId}`);
      toast.success('Document type deactivated');
      fetchDocuments();
    } catch (err) {
      toast.error('Failed to deactivate document type');
    }
  };

  const openEdit = (doc) => {
    setEditingDoc(doc);
    setForm({
      name: doc.name,
      description: doc.description || '',
      category: doc.category,
      is_mandatory: doc.is_mandatory,
      accepted_formats: doc.accepted_formats || ['PDF', 'JPG', 'PNG'],
      max_file_size_mb: doc.max_file_size_mb || 5,
      expiry_required: doc.expiry_required || false
    });
    setShowDialog(true);
  };

  const openCreate = () => {
    resetForm();
    setShowDialog(true);
  };

  const toggleFormat = (format) => {
    const formats = form.accepted_formats.includes(format)
      ? form.accepted_formats.filter(f => f !== format)
      : [...form.accepted_formats, format];
    setForm({ ...form, accepted_formats: formats });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'driver': return <Shield className="h-4 w-4" />;
      case 'vendor': return <Building2 className="h-4 w-4" />;
      case 'vehicle': return <Truck className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const filteredDocs = filter === 'all' 
    ? documents 
    : documents.filter(doc => doc.category === filter);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight" data-font="display">
            Document Compliance
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage document types for drivers, vendors, and vehicles
          </p>
        </div>
        <Button onClick={openCreate} data-testid="create-document-type">
          <Plus className="h-4 w-4 mr-2" /> Create Document Type
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2">
        <Button 
          variant={filter === 'all' ? 'default' : 'outline'} 
          size="sm"
          onClick={() => setFilter('all')}
        >
          All Documents
        </Button>
        {categories.map(cat => (
          <Button
            key={cat.value}
            variant={filter === cat.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(cat.value)}
          >
            {getCategoryIcon(cat.value)}
            <span className="ml-2">{cat.label}</span>
          </Button>
        ))}
      </div>

      {/* Document Types List */}
      <Card>
        <CardHeader>
          <CardTitle>Document Types ({filteredDocs.length})</CardTitle>
          <CardDescription>
            Configure required and optional documents for compliance
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-20" />)}
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No document types configured yet</p>
              <Button onClick={openCreate} className="mt-4">
                <Plus className="h-4 w-4 mr-2" /> Create First Document Type
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDocs.map(doc => (
                <div 
                  key={doc.id} 
                  className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
                  data-testid={`document-type-${doc.id}`}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="p-2 bg-primary/10 rounded">
                      {getCategoryIcon(doc.category)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium">{doc.name}</h3>
                        {doc.is_mandatory && (
                          <Badge variant="destructive" className="text-xs">
                            Mandatory
                          </Badge>
                        )}
                        {!doc.is_active && (
                          <Badge variant="secondary" className="text-xs">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">
                        {doc.description || 'No description'}
                      </p>
                      <div className="flex items-center gap-3 mt-2">
                        <span className="text-xs text-muted-foreground">
                          Formats: {doc.accepted_formats?.join(', ') || 'PDF, JPG, PNG'}
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className="text-xs text-muted-foreground">
                          Max size: {doc.max_file_size_mb}MB
                        </span>
                        {doc.expiry_required && (
                          <>
                            <span className="text-xs text-muted-foreground">•</span>
                            <span className="text-xs text-muted-foreground">
                              Expiry tracking enabled
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => openEdit(doc)}
                      data-testid={`edit-document-${doc.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      onClick={() => handleDelete(doc.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingDoc ? 'Edit Document Type' : 'Create Document Type'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Document Name *</Label>
              <Input
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Driver's License, Insurance Certificate"
                data-testid="document-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description of this document requirement"
                rows={3}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={form.category}
                  onValueChange={value => setForm({ ...form, category: value })}
                >
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Max File Size (MB)</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={form.max_file_size_mb}
                  onChange={e => setForm({ ...form, max_file_size_mb: parseInt(e.target.value) })}
                />
              </div>
            </div>

            <div className="space-y-3">
              <Label>Accepted Formats</Label>
              <div className="flex gap-3">
                {['PDF', 'JPG', 'PNG', 'JPEG', 'DOC', 'DOCX'].map(format => (
                  <label key={format} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.accepted_formats.includes(format)}
                      onChange={() => toggleFormat(format)}
                      className="rounded"
                    />
                    <span className="text-sm">{format}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="text-base">Mandatory Document</Label>
                <p className="text-sm text-muted-foreground">
                  Drivers/vendors must upload this document
                </p>
              </div>
              <Switch
                checked={form.is_mandatory}
                onCheckedChange={checked => setForm({ ...form, is_mandatory: checked })}
                data-testid="mandatory-switch"
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <Label className="text-base">Expiry Tracking</Label>
                <p className="text-sm text-muted-foreground">
                  Track expiration date for this document
                </p>
              </div>
              <Switch
                checked={form.expiry_required}
                onCheckedChange={checked => setForm({ ...form, expiry_required: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={editingDoc ? handleEdit : handleCreate}
              data-testid="save-document-type"
            >
              {editingDoc ? 'Save Changes' : 'Create Document Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
